import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalShipping as SupplierIcon,
  Payment as PaymentIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  AccountBalance as BalanceIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Phone as PhoneIcon,
  CreditCard as CreditIcon,
} from '@mui/icons-material';
import {
  useSuppliers,
  useCreateSupplier,
  useUpdateSupplier,
  useToggleSupplierStatus,
  useDeleteSupplier,
  useSupplierPayments,
  useAddSupplierPayment,
  useDeleteSupplierPayment,
  useSupplierRawMaterialUsage,
  useSupplyCategories,
  useCreateSupplyCategory,
  useUpdateSupplyCategory,
  useDeleteSupplyCategory,
} from '../hooks/useFeed';
import {
  useSupplyProcurements,
  useCreateSupplyProcurement,
  useUpdateSupplyProcurement,
  useDeleteSupplyProcurement,
  useMakeSupplyPayment,
} from '../hooks/useSupplyProcurements';
import { useBirdProcurements } from '../hooks/useBirds';
import { useQuery } from '@tanstack/react-query';
import { expenditureCategoriesService, feedService } from '../services/apiServices';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/format';

// ─── Constants ─────────────────────────────────────────────────────────────────

const COMMON_UNITS = ['bags', 'kg', 'litres', 'pieces', 'boxes', 'crates', 'tonnes'];

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  paid: { label: 'Paid', color: '#4caf50', bg: 'rgba(76,175,80,0.12)' },
  partial: { label: 'Partial', color: '#ff9800', bg: 'rgba(255,152,0,0.12)' },
  unpaid: { label: 'Unpaid', color: '#f44336', bg: 'rgba(244,67,54,0.12)' },
  'n/a': { label: 'Cash', color: '#888', bg: 'rgba(136,136,136,0.12)' },
};

const EMPTY_SUPPLY_FORM = {
  item: '',
  supplier_id: '',
  expenditure_category_id: '',
  batch_id: '',
  unit: '',
  quantity: '1',
  unit_price: '',
  amount_paid: '',
  is_credit: false,
  purchase_date: '',
  notes: '',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

const ghsToPesewas = (ghs: string | number) => Math.round((parseFloat(String(ghs)) || 0) * 100);
const pesewasToGhs = (p: number | string) => (parseFloat(String(p)) || 0) / 100;
const fmtDate = (d: string) =>
  d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

// ─── Design tokens ─────────────────────────────────────────────────────────────

const dataGridSx = {
  border: 0,
  background: 'transparent',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-columnHeaders': {
    background: 'rgba(255,152,0,0.05)',
    borderBottom: '1px solid rgba(255,152,0,0.12)',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.7px',
    color: 'rgba(255,152,0,0.6)',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row:hover': { background: 'rgba(255,152,0,0.04)' },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,152,0,0.1)',
    background: 'rgba(255,152,0,0.03)',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
    { color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' },
  '& .MuiTablePagination-select': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
  '& .MuiDataGrid-virtualScroller': { minHeight: 160 },
};

const dialogPaper = {
  borderRadius: 3,
  background: '#0e0e10',
  backgroundImage: 'none',
  border: '1px solid rgba(255,152,0,0.2)',
};

const formInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#111',
    color: '#e0e0e0',
    borderRadius: 1.5,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#ff9800' },
  },
  '& .MuiSelect-icon': { color: '#666' },
};

const formLabelSx = {
  color: '#888',
  fontSize: '0.72rem',
  fontWeight: 600,
  mb: 0.5,
  display: 'block',
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      color: 'rgba(255,255,255,0.3)',
      letterSpacing: '0.8px',
      fontSize: '0.62rem',
      textTransform: 'uppercase',
      display: 'block',
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent: string;
  Icon: React.ElementType;
}> = ({ label, value, sub, accent, Icon }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 160,
      p: 2.5,
      background: '#111318',
      border: '1px solid rgba(255,255,255,0.05)',
      borderLeft: `3px solid ${accent}`,
      borderRadius: 2.5,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: `inset 0 1px 0 ${accent}33`,
      transition: 'box-shadow 0.18s',
      '&:hover': {
        boxShadow: `inset 0 1px 0 ${accent}33, 0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px ${accent}22`,
      },
    }}
  >
    {/* Radial gradient overlay behind icon */}
    <Box
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        width: 90,
        height: '100%',
        background: `radial-gradient(ellipse at 80% 50%, ${accent}18 0%, transparent 70%)`,
        pointerEvents: 'none',
      }}
    />
    <Icon
      sx={{
        position: 'absolute',
        right: 14,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: 52,
        color: `${accent}0D`,
      }}
    />
    <Typography
      variant="caption"
      sx={{
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.7px',
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        display: 'block',
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h5"
      fontWeight={800}
      sx={{
        color: accent,
        lineHeight: 1,
        mb: sub ? 0.4 : 0,
        fontSize: typeof value === 'string' ? '1.25rem' : '1.8rem',
      }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ─── Supplier Card ─────────────────────────────────────────────────────────────

interface SupplierCardProps {
  supplier: any;
  supplyOutstanding: number;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onPayment: () => void;
}

const SupplierCard: React.FC<SupplierCardProps> = ({
  supplier,
  supplyOutstanding,
  onEdit,
  onToggle,
  onDelete,
  onPayment,
}) => {
  const rawBalance = parseFloat(supplier.balance_due ?? 0);
  const totalOwed = rawBalance;
  const initials = supplier.name
    .split(' ')
    .slice(0, 2)
    .map((w: string) => w[0])
    .join('')
    .toUpperCase();

  return (
    <Paper
      elevation={0}
      sx={{
        background: '#111318',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 2.5,
        overflow: 'hidden',
        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
          borderColor: totalOwed > 0 ? 'rgba(244,67,54,0.25)' : 'rgba(76,175,80,0.2)',
        },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Outstanding banner */}
      {totalOwed > 0 && (
        <Box
          sx={{
            px: 2.5,
            py: 0.75,
            background: 'linear-gradient(90deg, rgba(244,67,54,0.12) 0%, transparent 100%)',
            borderBottom: '1px solid rgba(244,67,54,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              color: 'rgba(244,67,54,0.7)',
              fontSize: '0.62rem',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontWeight: 700,
            }}
          >
            Outstanding
          </Typography>
          <Typography
            sx={{ color: '#ef5350', fontSize: '0.85rem', fontWeight: 800, fontFamily: 'monospace' }}
          >
            {formatCurrency(totalOwed, 0)}
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box sx={{ p: 2.5, pb: 2 }}>
        <Box display="flex" alignItems="flex-start" gap={1.5}>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              bgcolor: supplier.is_active ? 'rgba(255,152,0,0.15)' : 'rgba(255,255,255,0.05)',
              color: supplier.is_active ? '#ff9800' : 'rgba(255,255,255,0.3)',
              fontSize: '0.85rem',
              fontWeight: 800,
              border: `1.5px solid ${supplier.is_active ? 'rgba(255,152,0,0.3)' : 'rgba(255,255,255,0.08)'}`,
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography
                fontWeight={800}
                sx={{
                  color: '#ffffff',
                  fontSize: '0.92rem',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 160,
                }}
              >
                {supplier.name}
              </Typography>
              <Chip
                label={supplier.is_active ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  height: 17,
                  fontSize: '0.58rem',
                  fontWeight: 700,
                  bgcolor: supplier.is_active ? 'rgba(76,175,80,0.12)' : 'rgba(158,158,158,0.1)',
                  color: supplier.is_active ? '#4caf50' : '#9e9e9e',
                  border: `1px solid ${supplier.is_active ? 'rgba(76,175,80,0.3)' : 'rgba(158,158,158,0.2)'}`,
                }}
              />
            </Box>
            {supplier.phone && (
              <Box display="flex" alignItems="center" gap={0.5} mt={0.4}>
                <PhoneIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem' }}>
                  {supplier.phone}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {supplier.categories?.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={0.5} mt={1.5}>
            {supplier.categories.map((cat: any) => (
              <Chip
                key={cat.id}
                label={cat.name}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(255,152,0,0.08)',
                  color: '#ff9800',
                  border: '1px solid rgba(255,152,0,0.18)',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Financial summary */}
      <Box
        sx={{
          mx: 2.5,
          mb: 2,
          borderRadius: 1.5,
          border: '1px solid rgba(255,255,255,0.05)',
          overflow: 'hidden',
        }}
      >
        {/* Raw material row */}
        <Box
          sx={{
            p: 1.5,
            background: 'rgba(0,0,0,0.2)',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.2)',
              fontSize: '0.55rem',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              gridColumn: '1 / -1',
              mb: 0.5,
            }}
          >
            Raw Materials
          </Typography>
          {[
            {
              label: 'Owed',
              val: parseFloat(supplier.total_owed ?? 0),
              color: parseFloat(supplier.total_owed ?? 0) > 0 ? '#ef5350' : 'rgba(255,255,255,0.2)',
            },
            {
              label: 'Paid',
              val: parseFloat(supplier.total_paid ?? 0),
              color: parseFloat(supplier.total_paid ?? 0) > 0 ? '#66bb6a' : 'rgba(255,255,255,0.2)',
            },
            {
              label: 'Balance',
              val: rawBalance,
              color: rawBalance > 0 ? '#ffa726' : 'rgba(255,255,255,0.2)',
            },
          ].map(({ label, val, color }) => (
            <Box key={label} textAlign="center">
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.25)',
                  fontSize: '0.55rem',
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  color,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  lineHeight: 1.3,
                }}
              >
                {val > 0 ? formatCurrency(val, 0) : '—'}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Supply credit row */}
        {supplyOutstanding > 0 && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              background: 'rgba(244,67,54,0.05)',
              borderTop: '1px solid rgba(244,67,54,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.25)',
                fontSize: '0.6rem',
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
              }}
            >
              Supply credit due
            </Typography>
            <Typography
              sx={{
                color: '#ef5350',
                fontSize: '0.75rem',
                fontWeight: 700,
                fontFamily: 'monospace',
              }}
            >
              {formatCurrency(supplyOutstanding, 0)}
            </Typography>
          </Box>
        )}
      </Box>

      {supplier.notes && (
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.28)',
            fontSize: '0.68rem',
            px: 2.5,
            pb: 1.5,
            fontStyle: 'italic',
          }}
        >
          {supplier.notes}
        </Typography>
      )}

      {/* Actions */}
      <Box
        sx={{
          mt: 'auto',
          px: 2,
          py: 1.25,
          borderTop: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Tooltip title="Payments & Outstanding" arrow>
          <IconButton
            size="small"
            onClick={onPayment}
            sx={{
              color: totalOwed > 0 ? 'rgba(76,175,80,0.8)' : 'rgba(255,255,255,0.2)',
              '&:hover': { color: '#4caf50', bgcolor: 'rgba(76,175,80,0.1)' },
            }}
          >
            <PaymentIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit" arrow>
          <IconButton
            size="small"
            onClick={onEdit}
            sx={{
              color: 'rgba(255,152,0,0.6)',
              '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.1)' },
            }}
          >
            <EditIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={supplier.is_active ? 'Deactivate' : 'Activate'} arrow>
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{
              color: supplier.is_active ? 'rgba(244,67,54,0.5)' : 'rgba(76,175,80,0.5)',
              '&:hover': {
                color: supplier.is_active ? '#f44336' : '#4caf50',
                bgcolor: supplier.is_active ? 'rgba(244,67,54,0.08)' : 'rgba(76,175,80,0.08)',
              },
            }}
          >
            {supplier.is_active ? (
              <ToggleOnIcon sx={{ fontSize: 17 }} />
            ) : (
              <ToggleOffIcon sx={{ fontSize: 17 }} />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" arrow>
          <IconButton
            size="small"
            onClick={onDelete}
            sx={{
              ml: 'auto',
              color: 'rgba(244,67,54,0.4)',
              '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' },
            }}
          >
            <DeleteIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

// ─── Supply Procurement Form ───────────────────────────────────────────────────

const ProcurementForm: React.FC<{
  open: boolean;
  initial: any;
  suppliers: any[];
  categories: any[];
  batches: any[];
  onClose: () => void;
  onSave: (data: any) => void;
  saving: boolean;
}> = ({ open, initial, suppliers, categories, batches, onClose, onSave, saving }) => {
  const [form, setForm] = useState<any>(initial);

  React.useEffect(() => {
    setForm(initial);
  }, [initial]);

  const set = (field: string, val: any) => setForm((f: any) => ({ ...f, [field]: val }));

  const totalGhs = (parseFloat(form.quantity) || 0) * (parseFloat(form.unit_price) || 0);
  const amountPaidGhs = parseFloat(form.amount_paid) || 0;
  const canSave = !!form.item && !!form.purchase_date && !!form.unit_price && !!form.quantity;

  const handleSubmit = () => {
    const totalPesewas = ghsToPesewas(totalGhs);
    const paidPesewas = form.is_credit ? ghsToPesewas(form.amount_paid || 0) : totalPesewas;
    onSave({
      item: form.item,
      supplier_id: form.supplier_id || null,
      expenditure_category_id: form.expenditure_category_id || null,
      batch_id: form.batch_id || null,
      unit: form.unit || null,
      quantity: parseFloat(form.quantity) || 1,
      unit_price: ghsToPesewas(form.unit_price),
      amount_paid: paidPesewas,
      is_credit: form.is_credit,
      purchase_date: form.purchase_date,
      notes: form.notes || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            background: 'linear-gradient(145deg, #1a1a1a 0%, #222 100%)',
            border: '1px solid rgba(255,152,0,0.2)',
            borderRadius: 2.5,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogTitle sx={{ color: '#fff', fontWeight: 700, pb: 1, flexShrink: 0 }}>
        {initial?.id ? 'Edit Supply Purchase' : 'Record Supply Purchase'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
          overflowY: 'auto',
        }}
      >
        <Box>
          <Typography component="span" sx={formLabelSx}>
            Item *
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={form.item}
            onChange={(e) => set('item', e.target.value)}
            placeholder="e.g. Charcoal, Sawdust, Ivermectin"
            sx={formInputSx}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={formLabelSx}>
              Supplier
            </Typography>
            <Select
              fullWidth
              size="small"
              value={form.supplier_id}
              onChange={(e) => set('supplier_id', e.target.value)}
              displayEmpty
              sx={formInputSx}
            >
              <MenuItem value="">
                <em style={{ color: '#555' }}>No supplier</em>
              </MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={formLabelSx}>
              Category
            </Typography>
            <Select
              fullWidth
              size="small"
              value={form.expenditure_category_id}
              onChange={(e) => set('expenditure_category_id', e.target.value)}
              displayEmpty
              sx={formInputSx}
            >
              <MenuItem value="">
                <em style={{ color: '#555' }}>None</em>
              </MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Box>
          <Typography component="span" sx={formLabelSx}>
            Batch (Bird Procurement)
          </Typography>
          <Select
            fullWidth
            size="small"
            value={form.batch_id}
            onChange={(e) => set('batch_id', e.target.value)}
            displayEmpty
            sx={formInputSx}
          >
            <MenuItem value="">
              <em style={{ color: '#555' }}>Not linked to a batch</em>
            </MenuItem>
            {batches.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.identifier} — {b.date_delivered ?? b.date_procured}
                {b.total_quantity ? ` (${b.total_quantity} birds)` : ''}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography component="span" sx={formLabelSx}>
              Quantity *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
              value={form.quantity}
              onChange={(e) => set('quantity', e.target.value)}
              sx={formInputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography component="span" sx={formLabelSx}>
              Unit
            </Typography>
            <Select
              fullWidth
              size="small"
              value={form.unit}
              onChange={(e) => set('unit', e.target.value)}
              displayEmpty
              sx={formInputSx}
            >
              <MenuItem value="">
                <em style={{ color: '#555' }}>—</em>
              </MenuItem>
              {COMMON_UNITS.map((u) => (
                <MenuItem key={u} value={u}>
                  {u}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={formLabelSx}>
              Unit Price (GHS) *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              slotProps={{
                htmlInput: { min: 0, step: 0.01 },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: '#666', fontSize: '0.8rem' }}>GH₵</Typography>
                    </InputAdornment>
                  ),
                },
              }}
              value={form.unit_price}
              onChange={(e) => set('unit_price', e.target.value)}
              sx={formInputSx}
            />
          </Grid>
        </Grid>
        {totalGhs > 0 && (
          <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 600, pl: 0.5, mt: -1 }}>
            Total: {formatCurrency(totalGhs)}
          </Typography>
        )}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={formLabelSx}>
              Purchase Date *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={form.purchase_date}
              onChange={(e) => set('purchase_date', e.target.value)}
              sx={formInputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2.5 }}>
              <Switch
                size="small"
                checked={form.is_credit}
                onChange={(e) => {
                  set('is_credit', e.target.checked);
                  if (!e.target.checked) set('amount_paid', '');
                }}
                sx={{
                  '& .MuiSwitch-thumb': { bgcolor: form.is_credit ? '#f44336' : '#555' },
                  '& .MuiSwitch-track': {
                    bgcolor: form.is_credit ? 'rgba(244,67,54,0.4)' : 'rgba(255,255,255,0.1)',
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.82rem',
                  color: form.is_credit ? '#f44336' : '#666',
                  fontWeight: 600,
                }}
              >
                On Credit
              </Typography>
            </Box>
          </Grid>
        </Grid>
        {form.is_credit && (
          <Box>
            <Typography component="span" sx={formLabelSx}>
              Amount Paid Now (GHS)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              slotProps={{
                htmlInput: { min: 0, step: 0.01, max: totalGhs },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: '#666', fontSize: '0.8rem' }}>GH₵</Typography>
                    </InputAdornment>
                  ),
                },
              }}
              value={form.amount_paid}
              onChange={(e) => set('amount_paid', e.target.value)}
              placeholder={`0.00 — ${formatCurrency(totalGhs)} max`}
              sx={formInputSx}
            />
            {totalGhs > 0 && (
              <Typography variant="caption" sx={{ color: '#f44336', pl: 0.5 }}>
                Outstanding: {formatCurrency(totalGhs - amountPaidGhs)}
              </Typography>
            )}
          </Box>
        )}
        <Box>
          <Typography component="span" sx={formLabelSx}>
            Notes
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Optional notes…"
            sx={formInputSx}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          gap: 1,
          flexShrink: 0,
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Button onClick={onClose} sx={{ color: '#555' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !canSave}
          sx={{
            background: 'linear-gradient(135deg, #ff9800, #f57c00)',
            color: '#000',
            fontWeight: 700,
            px: 3,
          }}
        >
          {saving ? 'Saving…' : initial?.id ? 'Save Changes' : 'Record Purchase'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Supply Payment Dialog ─────────────────────────────────────────────────────

const SupplyPaymentDialog: React.FC<{
  row: any | null;
  onClose: () => void;
  onSave: (id: number, amountPesewas: number) => void;
  saving: boolean;
}> = ({ row, onClose, onSave, saving }) => {
  const [amount, setAmount] = useState('');

  React.useEffect(() => {
    if (row) setAmount('');
  }, [row]);

  const outstandingGhs = row ? pesewasToGhs(row.amount_left) : 0;
  const amountGhs = parseFloat(amount) || 0;

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            background: 'linear-gradient(145deg, #1a1a1a 0%, #222 100%)',
            border: '1px solid rgba(76,175,80,0.25)',
            borderRadius: 2.5,
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography fontWeight={700} sx={{ color: '#fff' }}>
          Record Payment
        </Typography>
        <Typography variant="caption" sx={{ color: '#555' }}>
          {row?.item}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: 'rgba(244,67,54,0.07)',
            borderRadius: 1.5,
            border: '1px solid rgba(244,67,54,0.15)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Total
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>
              {formatCurrency(pesewasToGhs(row?.total_amount ?? 0))}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Already paid
            </Typography>
            <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
              {formatCurrency(pesewasToGhs(row?.amount_paid ?? 0))}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 0.75 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Outstanding
            </Typography>
            <Typography sx={{ color: '#f44336', fontWeight: 800, fontSize: '1rem' }}>
              {formatCurrency(outstandingGhs)}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography component="span" sx={{ ...formLabelSx, color: '#888' }}>
            Amount to Pay (GHS)
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            autoFocus
            slotProps={{
              htmlInput: { min: 0.01, step: 0.01, max: outstandingGhs },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#4caf50', fontSize: '0.8rem', fontWeight: 700 }}>
                      GH₵
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={formatCurrency(outstandingGhs)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#111',
                color: '#e0e0e0',
                borderRadius: 1.5,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(76,175,80,0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#4caf50' },
              },
            }}
          />
          {amountGhs > 0 && amountGhs < outstandingGhs && (
            <Typography variant="caption" sx={{ color: '#ff9800', pl: 0.5 }}>
              Remaining after payment: {formatCurrency(outstandingGhs - amountGhs)}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setAmount(String(outstandingGhs))}
          sx={{
            alignSelf: 'flex-start',
            color: '#4caf50',
            borderColor: 'rgba(76,175,80,0.3)',
            fontSize: '0.72rem',
            '&:hover': { bgcolor: 'rgba(76,175,80,0.08)', borderColor: '#4caf50' },
          }}
        >
          Pay full balance
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: '#555' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(row.id, ghsToPesewas(amount))}
          disabled={saving || !amountGhs || amountGhs > outstandingGhs}
          sx={{
            background: 'linear-gradient(135deg, #4caf50, #388e3c)',
            color: '#fff',
            fontWeight: 700,
            px: 3,
          }}
        >
          {saving ? 'Saving…' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const SuppliersAndProcurements: React.FC = () => {
  const [tab, setTab] = useState(0);

  // ── Supplier hooks ───────────────────────────────────────────────────────
  const { data: allSuppliers = [] } = useSuppliers();
  const createSupplierMutation = useCreateSupplier();
  const updateSupplierMutation = useUpdateSupplier();
  const toggleSupplierStatusMutation = useToggleSupplierStatus();
  const deleteSupplierMutation = useDeleteSupplier();
  const { data: supplyCategories = [] } = useSupplyCategories();
  const createCategoryMutation = useCreateSupplyCategory();
  const updateCategoryMutation = useUpdateSupplyCategory();
  const deleteCategoryMutation = useDeleteSupplyCategory();
  const addPaymentMutation = useAddSupplierPayment();
  const deletePaymentMutation = useDeleteSupplierPayment();

  // ── Supply procurement hooks ─────────────────────────────────────────────
  const { data: supplyRaw, isFetching: isSupplyFetching } = useSupplyProcurements();
  const supplyItems: any[] = supplyRaw?.data ?? [];
  const { data: birdProcurementsRaw } = useBirdProcurements({ per_page: 200 });
  const batches: any[] = birdProcurementsRaw?.data ?? [];
  const createSupplyMutation = useCreateSupplyProcurement();
  const updateSupplyMutation = useUpdateSupplyProcurement();
  const deleteSupplyMutation = useDeleteSupplyProcurement();
  const supplyPaymentMutation = useMakeSupplyPayment();

  const { data: expenditureCategories = [] } = useQuery({
    queryKey: ['expenditure-categories'],
    queryFn: () => expenditureCategoriesService.getAll(),
  });

  const { data: suppliersForSelect } = useQuery({
    queryKey: ['feed-suppliers'],
    queryFn: () => feedService.getSuppliers(),
  });
  const suppliersForForm: any[] = suppliersForSelect ?? [];

  // ── Supplier payment dialog ──────────────────────────────────────────────
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPaymentSupplier, setSelectedPaymentSupplier] = useState<any>(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
  });
  const { data: paymentData, isLoading: paymentsLoading } = useSupplierPayments(
    selectedPaymentSupplier?.id
  );
  const { data: rawMaterialUsage } = useSupplierRawMaterialUsage(selectedPaymentSupplier?.id);
  const usageMaterials: any[] = rawMaterialUsage?.materials ?? [];

  // ── Supplier dialog ──────────────────────────────────────────────────────
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<any>(null);
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    phone: '',
    notes: '',
    category_ids: [] as number[],
  });

  // ── Category dialog ──────────────────────────────────────────────────────
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryName, setCategoryName] = useState('');

  // ── Supply procurement state ─────────────────────────────────────────────
  const [supplyFormOpen, setSupplyFormOpen] = useState(false);
  const [editingSupply, setEditingSupply] = useState<any>(null);
  const [supplyDeleteTarget, setSupplyDeleteTarget] = useState<any>(null);
  const [supplyPaymentTarget, setSupplyPaymentTarget] = useState<any>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // ── Derived data ─────────────────────────────────────────────────────────

  const suppliers = allSuppliers.filter((s: any) => s.categories?.length > 0);

  const supplyOutstandingBySupplier = useMemo(() => {
    const map: Record<number, number> = {};
    for (const item of supplyItems) {
      if (item.supplier_id && item.is_credit && item.amount_left > 0) {
        map[item.supplier_id] = (map[item.supplier_id] ?? 0) + pesewasToGhs(item.amount_left);
      }
    }
    return map;
  }, [supplyItems]);

  const selectedSupplyOutstanding = useMemo(() => {
    if (!selectedPaymentSupplier) return [];
    return supplyItems.filter(
      (i) => i.supplier_id === selectedPaymentSupplier.id && i.is_credit && i.amount_left > 0
    );
  }, [supplyItems, selectedPaymentSupplier]);

  const totalRawBalance = suppliers.reduce(
    (s: number, sup: any) => s + parseFloat(sup.balance_due ?? 0),
    0
  );
  const totalSupplyCredit = supplyItems.reduce(
    (s, i) => s + (i.is_credit ? pesewasToGhs(i.amount_left) : 0),
    0
  );
  const totalSupplySpend = supplyItems.reduce((s, i) => s + pesewasToGhs(i.total_amount), 0);

  const isMutating =
    addPaymentMutation.isPending ||
    deletePaymentMutation.isPending ||
    createSupplierMutation.isPending ||
    updateSupplierMutation.isPending ||
    toggleSupplierStatusMutation.isPending ||
    deleteSupplierMutation.isPending ||
    createCategoryMutation.isPending ||
    updateCategoryMutation.isPending ||
    deleteCategoryMutation.isPending ||
    createSupplyMutation.isPending ||
    updateSupplyMutation.isPending ||
    deleteSupplyMutation.isPending ||
    supplyPaymentMutation.isPending;

  // ── Handlers: suppliers ──────────────────────────────────────────────────

  const handleOpenSupplierDialog = (supplier?: any) => {
    setEditingSupplier(supplier ?? null);
    setSupplierForm({
      name: supplier?.name ?? '',
      phone: supplier?.phone ?? '',
      notes: supplier?.notes ?? '',
      category_ids: supplier?.categories?.map((c: any) => c.id) ?? [],
    });
    setSupplierDialogOpen(true);
  };

  const handleSaveSupplier = async () => {
    try {
      const payload = {
        name: supplierForm.name,
        phone: supplierForm.phone || undefined,
        notes: supplierForm.notes || undefined,
        category_ids: supplierForm.category_ids,
      };
      if (editingSupplier) {
        await updateSupplierMutation.mutateAsync({ id: editingSupplier.id, data: payload });
      } else {
        await createSupplierMutation.mutateAsync(payload);
      }
      setSupplierDialogOpen(false);
      setEditingSupplier(null);
    } catch {}
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;
    try {
      if (editingCategory) {
        await updateCategoryMutation.mutateAsync({
          id: editingCategory.id,
          data: { name: categoryName.trim() },
        });
      } else {
        await createCategoryMutation.mutateAsync({ name: categoryName.trim() });
      }
      setEditingCategory(null);
      setCategoryName('');
    } catch {}
  };

  const openPaymentDialog = (supplier: any) => {
    setSelectedPaymentSupplier(supplier);
    setPaymentDialogOpen(true);
  };

  const closePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedPaymentSupplier(null);
    setPaymentForm({ amount: '', payment_date: format(new Date(), 'yyyy-MM-dd'), notes: '' });
  };

  const handleAddPayment = async () => {
    if (!selectedPaymentSupplier) return;
    try {
      await addPaymentMutation.mutateAsync({
        supplierId: selectedPaymentSupplier.id,
        data: {
          amount: parseFloat(paymentForm.amount),
          payment_date: paymentForm.payment_date,
          notes: paymentForm.notes || undefined,
        },
      });
      setPaymentForm({ amount: '', payment_date: format(new Date(), 'yyyy-MM-dd'), notes: '' });
    } catch {}
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (!selectedPaymentSupplier || !window.confirm('Delete this payment record?')) return;
    try {
      await deletePaymentMutation.mutateAsync({
        supplierId: selectedPaymentSupplier.id,
        paymentId,
      });
    } catch {}
  };

  // ── Handlers: supply procurements ────────────────────────────────────────

  const openSupplyAdd = () => {
    setEditingSupply(null);
    setSupplyFormOpen(true);
  };

  const openSupplyEdit = (row: any) => {
    setEditingSupply({
      ...row,
      purchase_date: row.purchase_date?.substring(0, 10) ?? '',
      unit_price: String(pesewasToGhs(row.unit_price)),
      amount_paid: String(pesewasToGhs(row.amount_paid)),
      supplier_id: row.supplier_id ?? '',
      expenditure_category_id: row.expenditure_category_id ?? '',
      batch_id: row.batch_id ?? '',
      unit: row.unit ?? '',
    });
    setSupplyFormOpen(true);
  };

  const handleSaveSupply = (data: any) => {
    if (editingSupply?.id) {
      updateSupplyMutation.mutate(
        { id: editingSupply.id, data },
        {
          onSuccess: () => {
            setSupplyFormOpen(false);
            setSnack({ msg: 'Updated.', severity: 'success' });
          },
          onError: () => setSnack({ msg: 'Failed to update.', severity: 'error' }),
        }
      );
    } else {
      createSupplyMutation.mutate(data, {
        onSuccess: () => {
          setSupplyFormOpen(false);
          setSnack({ msg: 'Purchase recorded.', severity: 'success' });
        },
        onError: () => setSnack({ msg: 'Failed to save.', severity: 'error' }),
      });
    }
  };

  const handleSupplyPayment = (id: number, amount: number) => {
    supplyPaymentMutation.mutate(
      { id, amount },
      {
        onSuccess: () => {
          setSupplyPaymentTarget(null);
          setSnack({ msg: 'Payment recorded.', severity: 'success' });
        },
        onError: () => setSnack({ msg: 'Failed to record payment.', severity: 'error' }),
      }
    );
  };

  const handleDeleteSupply = () => {
    if (!supplyDeleteTarget) return;
    deleteSupplyMutation.mutate(supplyDeleteTarget.id, {
      onSuccess: () => {
        setSupplyDeleteTarget(null);
        setSnack({ msg: 'Deleted.', severity: 'success' });
      },
      onError: () => setSnack({ msg: 'Failed to delete.', severity: 'error' }),
    });
  };

  // ── DataGrid columns: supply procurements ────────────────────────────────

  const supplyColumns: GridColDef[] = [
    {
      field: 'item',
      headerName: 'Item',
      flex: 1.2,
      minWidth: 150,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.82rem' }}>
            {p.value}
          </Typography>
          {p.row.supplier?.name && (
            <Typography variant="caption" sx={{ color: '#555' }}>
              {p.row.supplier.name}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'expenditure_category_id',
      headerName: 'Category',
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ color: '#888' }}>
          {p.row.expenditure_category?.name ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Qty',
      width: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ color: '#aaa' }}>
          {parseFloat(p.value).toLocaleString()} {p.row.unit ?? ''}
        </Typography>
      ),
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.82rem' }}>
          {formatCurrency(pesewasToGhs(p.value))}
        </Typography>
      ),
    },
    {
      field: 'payment_status',
      headerName: 'Status',
      width: 140,
      renderCell: (p: GridRenderCellParams) => {
        const cfg = STATUS_CONFIG[p.value] ?? STATUS_CONFIG['n/a'];
        const outstandingGhs = pesewasToGhs(p.row.amount_left);
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Chip
              label={cfg.label}
              size="small"
              sx={{
                bgcolor: cfg.bg,
                color: cfg.color,
                fontWeight: 700,
                fontSize: '0.68rem',
                border: `1px solid ${cfg.color}33`,
                width: 'fit-content',
              }}
            />
            {outstandingGhs > 0 && (
              <Typography variant="caption" sx={{ color: '#f44336', fontSize: '0.65rem' }}>
                {formatCurrency(outstandingGhs)} due
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'purchase_date',
      headerName: 'Date',
      width: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ color: '#888' }}>
          {fmtDate(p.value)}
        </Typography>
      ),
    },
    {
      field: 'supply_actions',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {p.row.is_credit && p.row.amount_left > 0 && (
            <Tooltip title="Record Payment">
              <IconButton
                size="small"
                onClick={() => setSupplyPaymentTarget(p.row)}
                sx={{ color: '#4caf50', '&:hover': { bgcolor: 'rgba(76,175,80,0.1)' } }}
              >
                <PaymentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => openSupplyEdit(p.row)}
              sx={{ color: '#ff9800', '&:hover': { bgcolor: 'rgba(255,152,0,0.1)' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => setSupplyDeleteTarget(p.row)}
              sx={{ color: '#f44336', '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  const isLoading = isSupplyFetching || isMutating;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffcc02 50%, #ff9800 100%)',
            },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box
        sx={{
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0e0e10 0%, #1a1a1d 60%, #0e0e10 100%)',
          border: '1px solid rgba(255,152,0,0.12)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,152,0,0.06) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            p: { xs: 2.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            position: 'relative',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              flexShrink: 0,
              background:
                'linear-gradient(135deg, rgba(255,152,0,0.18) 0%, rgba(255,152,0,0.06) 100%)',
              border: '1px solid rgba(255,152,0,0.22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SupplierIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: '#ffffff',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                fontSize: { xs: '1.5rem', md: '1.9rem' },
              }}
            >
              Suppliers & Payments
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.4 }}>
              Manage suppliers, track outstanding balances, and record supply purchases
            </Typography>
          </Box>
          <Box display="flex" gap={1.5} flexShrink={0} flexWrap="wrap">
            <Button
              size="small"
              onClick={() => setManageCategoriesOpen(true)}
              sx={{
                color: 'rgba(255,255,255,0.5)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.25)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                },
              }}
            >
              Categories
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={openSupplyAdd}
              sx={{
                color: 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.3)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                },
              }}
            >
              Record Purchase
            </Button>
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleOpenSupplierDialog()}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                borderRadius: 2,
                px: 2.5,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                boxShadow: '0 4px 18px rgba(255,152,0,0.32)',
                '&:hover': { background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)' },
              }}
            >
              Add Supplier
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Stats Strip ── */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        <StatCard
          label="Suppliers"
          value={suppliers.length}
          sub="with categories"
          accent="#ff9800"
          Icon={SupplierIcon}
        />
        <StatCard
          label="Total Balance Due"
          value={formatCurrency(totalRawBalance, 0)}
          sub="owed across all suppliers"
          accent={totalRawBalance > 0 ? '#ef5350' : '#66bb6a'}
          Icon={InventoryIcon}
        />
        <StatCard
          label="Supply Credit Due"
          value={formatCurrency(totalSupplyCredit, 0)}
          sub={`of ${formatCurrency(totalSupplySpend, 0)} total spend`}
          accent={totalSupplyCredit > 0 ? '#ffa726' : '#66bb6a'}
          Icon={CreditIcon}
        />
        <StatCard
          label="Total Outstanding"
          value={formatCurrency(totalRawBalance, 0)}
          sub="combined across all suppliers"
          accent={totalRawBalance > 0 ? '#ef5350' : '#66bb6a'}
          Icon={BalanceIcon}
        />
      </Box>

      {/* ── Tabs Panel ── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid rgba(255,152,0,0.12)',
          borderRadius: 2.5,
          overflow: 'hidden',
          background: '#0e0e10',
        }}
      >
        <Box
          sx={{
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            px: 2,
            background: 'linear-gradient(90deg, rgba(255,152,0,0.05) 0%, transparent 100%)',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.4)',
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48,
                fontSize: '0.82rem',
              },
              '& .Mui-selected': { color: '#ff9800' },
              '& .MuiTabs-indicator': { backgroundColor: '#ff9800', height: 2 },
            }}
          >
            <Tab
              icon={<SupplierIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label={`Suppliers (${suppliers.length})`}
            />
            <Tab
              icon={<CategoryIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label={`Supply Purchases (${supplyItems.length})`}
            />
          </Tabs>
        </Box>

        {/* Tab 0: Suppliers */}
        {tab === 0 && (
          <Box p={2.5}>
            {suppliers.length === 0 ? (
              <Box py={8} textAlign="center">
                <SupplierIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.08)', mb: 2 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
                  No suppliers yet.
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.18)', fontSize: '0.78rem', mt: 0.5 }}>
                  Add a supplier and assign supply categories.
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenSupplierDialog()}
                  variant="outlined"
                  size="small"
                  sx={{
                    mt: 2.5,
                    color: '#ff9800',
                    borderColor: 'rgba(255,152,0,0.4)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#ff9800', bgcolor: 'rgba(255,152,0,0.06)' },
                  }}
                >
                  Add First Supplier
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {suppliers.map((supplier: any) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={supplier.id}>
                    <SupplierCard
                      supplier={supplier}
                      supplyOutstanding={supplyOutstandingBySupplier[supplier.id] ?? 0}
                      onEdit={() => handleOpenSupplierDialog(supplier)}
                      onToggle={() => toggleSupplierStatusMutation.mutate(supplier.id)}
                      onDelete={() => {
                        if (window.confirm(`Delete supplier "${supplier.name}"?`))
                          deleteSupplierMutation.mutate(supplier.id);
                      }}
                      onPayment={() => openPaymentDialog(supplier)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Tab 1: Supply Purchases */}
        {tab === 1 && (
          <Box>
            <Box
              sx={{
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid rgba(255,152,0,0.1)',
              }}
            >
              <CategoryIcon sx={{ color: '#ff9800', fontSize: '1.1rem' }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>
                All Supply Purchases
              </Typography>
              <Chip
                label={supplyItems.length}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,152,0,0.12)',
                  color: '#ff9800',
                  fontWeight: 700,
                  height: 20,
                }}
              />
              <Box flex={1} />
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={openSupplyAdd}
                sx={{
                  color: '#ff9800',
                  borderColor: 'rgba(255,152,0,0.4)',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  borderRadius: 1.5,
                }}
              >
                Record Purchase
              </Button>
            </Box>
            <DataGrid
              rows={supplyItems}
              columns={supplyColumns}
              autoHeight
              disableRowSelectionOnClick
              getRowHeight={() => 64}
              sx={dataGridSx}
            />
          </Box>
        )}
      </Paper>

      {/* ══ Supplier Payments & Outstanding Dialog ══ */}
      <Dialog
        open={paymentDialogOpen}
        onClose={closePaymentDialog}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#ffffff' }}>
            Payments & Outstanding
          </Typography>
          <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 600 }}>
            {selectedPaymentSupplier?.name}
          </Typography>
        </Box>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
          {paymentsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress sx={{ color: '#ff9800' }} />
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pt: 3 }}>
                <Box display="flex" flexDirection="column" gap={3}>
                  {/* Balance strip */}
                  <Box display="flex" gap={2} flexWrap="wrap">
                    {[
                      {
                        label: 'Raw Material Owed',
                        value: paymentData?.total_owed ?? selectedPaymentSupplier?.total_owed ?? 0,
                        color: '#ef5350',
                      },
                      {
                        label: 'Raw Material Paid',
                        value: paymentData?.total_paid ?? selectedPaymentSupplier?.total_paid ?? 0,
                        color: '#66bb6a',
                      },
                      {
                        label: 'Raw Material Balance',
                        value:
                          paymentData?.balance_due ?? selectedPaymentSupplier?.balance_due ?? 0,
                        color:
                          parseFloat(
                            paymentData?.balance_due ?? selectedPaymentSupplier?.balance_due ?? 0
                          ) > 0
                            ? '#ffa726'
                            : '#66bb6a',
                      },
                      {
                        label: 'Supply Credit Due',
                        value: supplyOutstandingBySupplier[selectedPaymentSupplier?.id] ?? 0,
                        color:
                          (supplyOutstandingBySupplier[selectedPaymentSupplier?.id] ?? 0) > 0
                            ? '#ef5350'
                            : 'rgba(255,255,255,0.2)',
                      },
                    ].map(({ label, value, color }) => (
                      <Paper
                        key={label}
                        elevation={0}
                        sx={{
                          flex: 1,
                          minWidth: 120,
                          p: 2,
                          background: '#111318',
                          border: '1px solid rgba(255,255,255,0.05)',
                          borderLeft: `3px solid ${color}`,
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255,255,255,0.35)',
                            fontSize: '0.62rem',
                            letterSpacing: '0.6px',
                            textTransform: 'uppercase',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={800}
                          sx={{
                            color,
                            lineHeight: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.95rem',
                          }}
                        >
                          {formatCurrency(value as string)}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  {/* Raw material usage — payment windows */}
                  {usageMaterials.length > 0 && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Previous window */}
                      {rawMaterialUsage?.prev_window_total > 0 && (
                        <Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="baseline"
                            mb={0.75}
                          >
                            <SectionLabel>Previous Window</SectionLabel>
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}
                            >
                              {rawMaterialUsage?.prev_window_start} →{' '}
                              {rawMaterialUsage?.prev_window_end}
                            </Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" gap={0.75} mb={1}>
                            {usageMaterials
                              .filter((m: any) => m.prev_qty_used > 0)
                              .map((m: any) => (
                                <Paper
                                  key={m.raw_material_id}
                                  elevation={0}
                                  sx={{
                                    px: 2.5,
                                    py: 1.25,
                                    background: '#111318',
                                    border: '1px solid rgba(255,255,255,0.05)',
                                    borderRadius: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight={700}
                                      sx={{ color: '#fff', fontSize: '0.82rem' }}
                                    >
                                      {m.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                                    >
                                      {m.prev_qty_used.toLocaleString()} {m.unit} ·{' '}
                                      {formatCurrency(m.cost_per_unit)}/{m.unit}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight={800}
                                    sx={{ color: 'rgba(255,167,38,0.6)', fontFamily: 'monospace' }}
                                  >
                                    {formatCurrency(m.prev_cost)}
                                  </Typography>
                                </Paper>
                              ))}
                          </Box>
                          <Paper
                            elevation={0}
                            sx={{
                              px: 2.5,
                              py: 1.25,
                              background: 'rgba(255,152,0,0.04)',
                              border: '1px solid rgba(255,152,0,0.12)',
                              borderRadius: 2,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Previous window total
                              </Typography>
                              {rawMaterialUsage?.prev_window_paid > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#66bb6a', fontSize: '0.68rem' }}
                                >
                                  Paid: {formatCurrency(rawMaterialUsage?.prev_window_paid)}
                                </Typography>
                              )}
                            </Box>
                            <Box textAlign="right">
                              {rawMaterialUsage?.prev_window_paid > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'rgba(255,255,255,0.3)',
                                    fontFamily: 'monospace',
                                    display: 'block',
                                    textDecoration: 'line-through',
                                  }}
                                >
                                  {formatCurrency(rawMaterialUsage?.prev_window_total ?? 0)}
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                fontWeight={800}
                                sx={{
                                  color:
                                    rawMaterialUsage?.prev_window_balance > 0
                                      ? 'rgba(255,167,38,0.7)'
                                      : '#66bb6a',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {formatCurrency(rawMaterialUsage?.prev_window_balance ?? 0)}
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      )}

                      {/* Current window */}
                      <Box>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="baseline"
                          mb={0.75}
                        >
                          <SectionLabel>This Week&apos;s Usage</SectionLabel>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}
                          >
                            {rawMaterialUsage?.window_start} → {rawMaterialUsage?.window_end}
                          </Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.75} mb={1}>
                          {usageMaterials.map((m: any) => (
                            <Paper
                              key={m.raw_material_id}
                              elevation={0}
                              sx={{
                                px: 2.5,
                                py: 1.25,
                                background: '#111318',
                                border: '1px solid rgba(255,255,255,0.05)',
                                borderRadius: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{ color: '#fff', fontSize: '0.82rem' }}
                                >
                                  {m.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                                >
                                  {m.window_qty_used.toLocaleString()} {m.unit} ·{' '}
                                  {formatCurrency(m.cost_per_unit)}/{m.unit}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                fontWeight={800}
                                sx={{
                                  color: m.window_cost > 0 ? '#ffa726' : 'rgba(255,255,255,0.2)',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {formatCurrency(m.window_cost)}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                        {/* Window total + quick pay */}
                        <Paper
                          elevation={0}
                          sx={{
                            px: 2.5,
                            py: 1.5,
                            background: 'rgba(255,152,0,0.06)',
                            border: '1px solid rgba(255,152,0,0.2)',
                            borderRadius: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={700} sx={{ color: '#fff' }}>
                              {rawMaterialUsage?.window_paid > 0
                                ? 'Balance this window'
                                : 'Total owed this window'}
                            </Typography>
                            {rawMaterialUsage?.window_paid > 0 && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#66bb6a', fontSize: '0.68rem' }}
                              >
                                {formatCurrency(rawMaterialUsage?.window_paid)} already paid · was{' '}
                                {formatCurrency(rawMaterialUsage?.window_total_owed)}
                              </Typography>
                            )}
                            <Typography
                              variant="h6"
                              fontWeight={800}
                              sx={{
                                color:
                                  rawMaterialUsage?.window_balance === 0 ? '#66bb6a' : '#ffa726',
                                fontFamily: 'monospace',
                                fontSize: '1rem',
                              }}
                            >
                              {formatCurrency(
                                rawMaterialUsage?.window_balance ??
                                  rawMaterialUsage?.window_total_owed ??
                                  0
                              )}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={
                              !rawMaterialUsage?.window_balance || addPaymentMutation.isPending
                            }
                            onClick={() =>
                              setPaymentForm((f) => ({
                                ...f,
                                amount: String(rawMaterialUsage?.window_balance ?? ''),
                                notes: `Payment for window ${rawMaterialUsage?.window_start} – ${rawMaterialUsage?.window_end}`,
                              }))
                            }
                            sx={{
                              bgcolor: '#ffa726',
                              color: '#000',
                              fontWeight: 700,
                              '&:hover': { bgcolor: '#ff9800' },
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Pay Now
                          </Button>
                        </Paper>
                      </Box>
                    </Box>
                  )}

                  {/* Outstanding supply items */}
                  {selectedSupplyOutstanding.length > 0 && (
                    <Box>
                      <SectionLabel>Outstanding Supply Purchases</SectionLabel>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {selectedSupplyOutstanding.map((item: any) => (
                          <Paper
                            key={item.id}
                            elevation={0}
                            sx={{
                              px: 2.5,
                              py: 1.5,
                              background: 'rgba(244,67,54,0.05)',
                              border: '1px solid rgba(244,67,54,0.1)',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ color: '#ffffff', fontSize: '0.82rem' }}
                              >
                                {item.item}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                              >
                                {fmtDate(item.purchase_date)} ·{' '}
                                {formatCurrency(pesewasToGhs(item.total_amount))} total
                              </Typography>
                            </Box>
                            <Box textAlign="right">
                              <Typography
                                sx={{
                                  color: '#ef5350',
                                  fontWeight: 800,
                                  fontFamily: 'monospace',
                                  fontSize: '0.9rem',
                                }}
                              >
                                {formatCurrency(pesewasToGhs(item.amount_left))}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}
                              >
                                outstanding
                              </Typography>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Raw material payment history */}
                  <Box>
                    <SectionLabel>Raw Material Payment History</SectionLabel>
                    {!paymentData?.payments?.length ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No raw material payments recorded yet.
                      </Alert>
                    ) : (
                      <Box display="flex" flexDirection="column" gap={1}>
                        {paymentData.payments.map((payment: any) => (
                          <Paper
                            key={payment.id}
                            elevation={0}
                            sx={{
                              px: 2.5,
                              py: 1.5,
                              background: '#111318',
                              border: '1px solid rgba(255,255,255,0.05)',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ color: '#66bb6a', fontFamily: 'monospace' }}
                              >
                                {formatCurrency(payment.amount)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}
                              >
                                {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                                {payment.notes ? ` · ${payment.notes}` : ''}
                                {payment.recorded_by?.name ? ` · ${payment.recorded_by.name}` : ''}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleDeletePayment(payment.id)}
                              disabled={deletePaymentMutation.isPending}
                              sx={{
                                color: 'rgba(244,67,54,0.5)',
                                '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* ── Sticky payment form ── */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              >
                <SectionLabel>Log Raw Material Payment</SectionLabel>
                <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="flex-start">
                  <TextField
                    label="Amount (GH₵)"
                    type="number"
                    size="small"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    sx={{
                      flex: 1,
                      minWidth: 130,
                      '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                    }}
                    slotProps={{ htmlInput: { min: '0.01', step: '0.01' } }}
                    required
                  />
                  <TextField
                    label="Date"
                    type="date"
                    size="small"
                    value={paymentForm.payment_date}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, payment_date: e.target.value })
                    }
                    sx={{
                      flex: 1,
                      minWidth: 140,
                      '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                    required
                  />
                  <TextField
                    label="Notes"
                    size="small"
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder="e.g. Week 1"
                    sx={{
                      flex: 1.5,
                      minWidth: 160,
                      '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddPayment}
                    disabled={
                      !paymentForm.amount ||
                      !paymentForm.payment_date ||
                      addPaymentMutation.isPending
                    }
                    startIcon={
                      addPaymentMutation.isPending ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : null
                    }
                    sx={{
                      background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 1.5,
                      px: 2.5,
                      alignSelf: 'center',
                    }}
                  >
                    {addPaymentMutation.isPending ? 'Saving…' : 'Log Payment'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closePaymentDialog}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Add/Edit Supplier Dialog ══ */}
      <Dialog
        open={supplierDialogOpen}
        onClose={() => {
          setSupplierDialogOpen(false);
          setEditingSupplier(null);
        }}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#ffffff' }}>
            {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Supplier Name"
              value={supplierForm.name}
              onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
              fullWidth
              required
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <TextField
              label="Phone"
              value={supplierForm.phone}
              onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
              fullWidth
              placeholder="+233 XX XXX XXXX"
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <Autocomplete
              multiple
              options={supplyCategories}
              getOptionLabel={(o: any) => o.name}
              value={supplyCategories.filter((c: any) => supplierForm.category_ids.includes(c.id))}
              onChange={(_, val: any[]) =>
                setSupplierForm({ ...supplierForm, category_ids: val.map((c) => c.id) })
              }
              isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="What do they supply?"
                  placeholder="Select categories…"
                  helperText={
                    supplyCategories.length === 0
                      ? 'No categories yet — use Categories to add some'
                      : undefined
                  }
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              )}
            />
            <TextField
              label="Notes"
              multiline
              rows={2}
              size="small"
              value={supplierForm.notes}
              onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => {
              setSupplierDialogOpen(false);
              setEditingSupplier(null);
            }}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSupplier}
            variant="contained"
            disabled={
              !supplierForm.name ||
              createSupplierMutation.isPending ||
              updateSupplierMutation.isPending
            }
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {createSupplierMutation.isPending || updateSupplierMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : editingSupplier ? (
              'Save Changes'
            ) : (
              'Add Supplier'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Manage Categories Dialog ══ */}
      <Dialog
        open={manageCategoriesOpen}
        onClose={() => {
          setManageCategoriesOpen(false);
          setEditingCategory(null);
          setCategoryName('');
        }}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 2.5, borderBottom: '1px solid rgba(255,152,0,0.1)' }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#ffffff' }}>
            Supply Categories
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" gap={1} mb={2.5}>
            <TextField
              label={editingCategory ? 'Edit name' : 'New category'}
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              size="small"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveCategory();
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <Button
              variant="contained"
              onClick={handleSaveCategory}
              disabled={
                !categoryName.trim() ||
                createCategoryMutation.isPending ||
                updateCategoryMutation.isPending
              }
              sx={{
                whiteSpace: 'nowrap',
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1.5,
                px: 2,
              }}
            >
              {editingCategory ? 'Save' : 'Add'}
            </Button>
            {editingCategory && (
              <Button
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryName('');
                }}
                sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.45)', borderRadius: 1.5 }}
              >
                Cancel
              </Button>
            )}
          </Box>
          {supplyCategories.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', py: 2 }}
            >
              No categories yet.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {supplyCategories.map((cat: any) => (
                <Paper
                  key={cat.id}
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1.25,
                    background: '#111318',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Chip
                    label={cat.name}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: 'rgba(255,152,0,0.1)',
                      color: '#ff9800',
                      border: '1px solid rgba(255,152,0,0.2)',
                    }}
                  />
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingCategory(cat);
                        setCategoryName(cat.name);
                      }}
                      sx={{
                        color: 'rgba(255,152,0,0.6)',
                        '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.08)' },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (window.confirm(`Delete "${cat.name}"?`))
                          deleteCategoryMutation.mutate(cat.id);
                      }}
                      sx={{
                        color: 'rgba(244,67,54,0.5)',
                        '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setManageCategoriesOpen(false);
              setEditingCategory(null);
              setCategoryName('');
            }}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Supply Procurement Form ══ */}
      <ProcurementForm
        open={supplyFormOpen}
        initial={editingSupply ?? { ...EMPTY_SUPPLY_FORM }}
        suppliers={suppliersForForm}
        categories={expenditureCategories}
        batches={batches}
        onClose={() => setSupplyFormOpen(false)}
        onSave={handleSaveSupply}
        saving={createSupplyMutation.isPending || updateSupplyMutation.isPending}
      />

      {/* ══ Supply Payment Dialog ══ */}
      <SupplyPaymentDialog
        row={supplyPaymentTarget}
        onClose={() => setSupplyPaymentTarget(null)}
        onSave={handleSupplyPayment}
        saving={supplyPaymentMutation.isPending}
      />

      {/* ══ Delete Supply Confirmation ══ */}
      <Dialog
        open={!!supplyDeleteTarget}
        onClose={() => setSupplyDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              background: 'linear-gradient(145deg, #1a1a1a 0%, #222 100%)',
              border: '1px solid rgba(244,67,54,0.25)',
              borderRadius: 2.5,
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>Delete Record</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Remove <strong style={{ color: '#ffa726' }}>{supplyDeleteTarget?.item}</strong> from
            records? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setSupplyDeleteTarget(null)} sx={{ color: '#555' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteSupply}
            disabled={deleteSupplyMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #f44336, #c62828)',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            {deleteSupplyMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack?.severity ?? 'success'}
          onClose={() => setSnack(null)}
          sx={{ fontWeight: 600 }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuppliersAndProcurements;
